import { useState } from "react";
import { useParams, useNavigate, generatePath, Link as RouterLink } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box, Typography, Grid, Card, CardContent, Chip, Button, Divider,
  CircularProgress, Alert, Snackbar, Stack, Link, ImageList, ImageListItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem,
  FormControl, InputLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ImageWithLoader from "../../../common-components/ImageWithLoader";
import { resolveImageUri } from "../../../utility/constants";
import {
  getAdminProductDetail,
  updateProductStatus,
  resetProductChangeLimit,
  setProductRecommendationVisibility,
} from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";

const statusColor = {
  DRAFT: "default",
  PUBLISHED: "success",
  ARCHIVED: "default",
  OUT_OF_STOCK: "warning",
  SUSPENDED: "error",
};

const accountStatusColor = (status) =>
  status === "ACTIVE" ? "success" : status === "SUSPENDED" ? "warning" : "error";

const formatCurrency = (cents, currency) => {
  const amount = ((cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${amount}` : amount;
};

const formatDateTime = (date) =>
  date ? new Date(date).toLocaleString(undefined, { timeZoneName: "short" }) : "—";

const ProductImages = ({ images }) => {
  const [lightbox, setLightbox] = useState(null);
  if (!images?.length) return null;
  return (
    <>
      <ImageList cols={3} gap={8}>
        {images.map((img, i) => (
          <ImageListItem key={img._id || i} sx={{ cursor: "pointer" }} onClick={() => setLightbox(img)}>
            <ImageWithLoader src={resolveImageUri(img)} alt={`product-${i}`} sx={{ height: 120, borderRadius: "4px" }} />
          </ImageListItem>
        ))}
      </ImageList>
      <Dialog open={!!lightbox} onClose={() => setLightbox(null)} maxWidth="md">
        <DialogContent sx={{ p: 0 }}>
          <ImageWithLoader
            src={lightbox ? resolveImageUri(lightbox) : ""}
            alt="product"
            objectFit="contain"
            sx={{ width: "100%", height: { xs: 320, md: 480 } }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const ProductStatusDialog = ({ open, currentStatus, onClose, onConfirm, loading }) => {
  const [targetStatus, setTargetStatus] = useState("PUBLISHED");
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{currentStatus === "SUSPENDED" ? "Restore Product" : "Admin Delist Product"}</DialogTitle>
      <DialogContent>
        {currentStatus === "SUSPENDED" ? (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Restore to</InputLabel>
            <Select value={targetStatus} label="Restore to" onChange={(e) => setTargetStatus(e.target.value)}>
              <MenuItem value="PUBLISHED">Published</MenuItem>
              <MenuItem value="ARCHIVED">Archived</MenuItem>
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" mt={1}>This will set the product status to SUSPENDED and hide it from public view.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={currentStatus === "SUSPENDED" ? "success" : "error"}
          disabled={loading}
          onClick={() => onConfirm(currentStatus === "SUSPENDED" ? targetStatus : "SUSPENDED")}
        >
          {loading ? <CircularProgress size={18} /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-product", productId],
    queryFn: () => getAdminProductDetail(productId),
  });

  const statusMutation = useMutation({
    mutationFn: (status) => updateProductStatus(productId, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-product", productId] });
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      setStatusDialogOpen(false);
      setSnackbar({ severity: "success", message: "Product status updated." });
    },
    onError: (err) => setSnackbar({ severity: "error", message: err?.data?.message || "Failed." }),
  });

  const resetLimitMutation = useMutation({
    mutationFn: (field) => resetProductChangeLimit(productId, { field }),
    onSuccess: (_, field) => {
      qc.invalidateQueries({ queryKey: ["admin-product", productId] });
      setSnackbar({ severity: "success", message: `Product ${field} change limit reset.` });
    },
    onError: (err) => setSnackbar({ severity: "error", message: err?.data?.message || "Failed to reset limit." }),
  });

  const recommendationsMutation = useMutation({
    mutationFn: ({ hidden }) => setProductRecommendationVisibility(productId, { hidden }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-product", productId] });
      setSnackbar({ severity: "success", message: "Recommendation visibility updated." });
    },
    onError: (err) => setSnackbar({ severity: "error", message: err?.data?.message || "Failed." }),
  });

  if (isLoading) return <CircularProgress />;
  if (error || !data?.product) return <Alert severity="error">Product not found.</Alert>;

  const { product, reports } = data;
  const shop = product.shop;
  const owner = shop?.createdBy;

  const nameLocked = product.nameLastChangedAt &&
    new Date(new Date(product.nameLastChangedAt).getTime() + 60 * 24 * 60 * 60 * 1000) > new Date();
  const catLocked = product.categoryLastChangedAt &&
    new Date(new Date(product.categoryLastChangedAt).getTime() + 30 * 24 * 60 * 60 * 1000) > new Date();

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(frontendRoutes.ADMIN_PRODUCTS)} sx={{ mb: 2 }}>
        Back to Products
      </Button>

      <Typography variant="h5" fontWeight={700} mb={1}>{product.productName}</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <Chip label={product.status} color={statusColor[product.status] || "default"} />
        {product.hiddenFromRecommendations && <Chip label="Hidden from feed" />}
      </Stack>

      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} md={7}>
          {product.images?.length > 0 && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <ProductImages images={product.images} />
              </CardContent>
            </Card>
          )}

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>Product Info</Typography>
              <Typography variant="body2" whiteSpace="pre-wrap">{product.description}</Typography>
              {product.orderInstructions && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>Order Instructions</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap">{product.orderInstructions}</Typography>
                </>
              )}
              {product.deliveryInstructions && (
                <>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>Delivery Instructions</Typography>
                  <Typography variant="body2" whiteSpace="pre-wrap">{product.deliveryInstructions}</Typography>
                </>
              )}
              {product.categories?.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={1}>
                  {product.categories.map((c) => (
                    <Chip key={c} label={c.replace(/_/g, " ")} size="small" />
                  ))}
                </Stack>
              )}
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                {product.pricingMode === "OFFERS"
                  ? "Asking price (hidden from buyers)"
                  : "Price"}
                : {formatCurrency(product.priceCents, product.currency)}
              </Typography>
              <Typography variant="body2">
                Pricing:{" "}
                {product.pricingMode === "RANGE"
                  ? "Price range"
                  : product.pricingMode === "OFFERS"
                    ? "Hidden price (buyers make an offer)"
                    : product.negotiable
                      ? "Fixed, open to offers"
                      : "Fixed"}
              </Typography>
              {product.pricingMode === "RANGE" ? (
                <Typography variant="body2">
                  Lowest price considered: {formatCurrency(product.minPriceCents, product.currency)}
                </Typography>
              ) : product.pricingMode === "OFFERS" || product.negotiable ? (
                <Typography variant="body2">
                  Lowest offer considered (hidden from buyers):{" "}
                  {product.minOfferCents != null ? formatCurrency(product.minOfferCents, product.currency) : "Not set"}
                </Typography>
              ) : null}
              <Typography variant="body2">Delivery fee: {formatCurrency(product.deliveryFeeCents || 0, product.currency)}</Typography>
              <Typography variant="body2">Delivery methods: {product.deliveryMethods?.join(", ") || "—"}</Typography>
              <Typography variant="body2">Payment methods: {product.paymentMethods?.join(", ") || "—"}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">Stock: {product.stockQuantity ?? "Unlimited"}</Typography>
              <Typography variant="body2">Reserved: {product.reservedQuantity || 0}</Typography>
              <Typography variant="body2">Auto-delist on stock-out: {product.autoDelistOnStockOut ? "Yes" : "No"}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">Rating: {product.rating ? product.rating.toFixed(1) : "—"} ({product.numReviews || 0} reviews)</Typography>
              <Typography variant="body2">Sold: {product.numSold || 0}</Typography>
              <Typography variant="body2">Views: {product.numViews || 0}</Typography>
              <Typography variant="body2">Revenue: {formatCurrency(product.revenueFromSalesCents, product.currency)}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">Created: {formatDateTime(product.createdAt)}</Typography>
              <Typography variant="body2">Updated: {formatDateTime(product.updatedAt)}</Typography>
            </CardContent>
          </Card>

          {/* Change limits */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={0.5}>Change Limits</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Reset a field lock so the shop owner can update it immediately.
              </Typography>
              {[
                { label: "Product Name", field: "name", locked: nameLocked, lastChangedAt: product.nameLastChangedAt },
                { label: "Categories", field: "categories", locked: catLocked, lastChangedAt: product.categoryLastChangedAt },
              ].map(({ label, field, locked, lastChangedAt }) => (
                <Stack key={field} direction="row" alignItems="center" justifyContent="space-between" py={0.75}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{label}</Typography>
                    {lastChangedAt ? (
                      <Typography variant="caption" color={locked ? "warning.main" : "text.secondary"}>
                        {locked ? "Locked" : "Unlocked"} — last changed {formatDateTime(lastChangedAt)}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Never changed</Typography>
                    )}
                  </Box>
                  {locked && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      disabled={resetLimitMutation.isPending}
                      onClick={() => resetLimitMutation.mutate(field)}
                    >
                      Reset
                    </Button>
                  )}
                </Stack>
              ))}
            </CardContent>
          </Card>

          {/* Reports */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>Reports Against This Product ({reports?.length || 0})</Typography>
              {reports?.length ? reports.map((r) => (
                <Box key={r._id} py={0.5}>
                  <Typography variant="body2">
                    {r.reason?.replace(/_/g, " ")} — <Chip label={r.status} size="small" /> — {formatDateTime(r.createdAt)}
                  </Typography>
                </Box>
              )) : <Typography variant="body2" color="text.secondary">No reports.</Typography>}
            </CardContent>
          </Card>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={0.5}>Shop</Typography>
              {shop?._id ? (
                <Link
                  component={RouterLink}
                  to={generatePath(frontendRoutes.ADMIN_SHOP_DETAIL, { shopId: shop._id })}
                  underline="hover"
                  variant="body2"
                  sx={{ display: "block" }}
                >
                  {shop?.shopName || "—"}
                </Link>
              ) : (
                <Typography variant="body2">{shop?.shopName || "—"}</Typography>
              )}
              {shop?.status && shop.status !== "ACTIVE" && (
                <Chip label={shop.status} color="warning" size="small" sx={{ mt: 0.5, mb: 0.5 }} />
              )}
              <Typography variant="body2" color="text.secondary">{shop?.country || "—"}</Typography>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={0.5}>Owner</Typography>
              {owner ? (
                <>
                  {owner._id ? (
                    <Link
                      component={RouterLink}
                      to={generatePath(frontendRoutes.ADMIN_USER_DETAIL, { userId: owner._id })}
                      underline="hover"
                      variant="body2"
                      sx={{ display: "block" }}
                    >
                      {owner.name}
                    </Link>
                  ) : (
                    <Typography variant="body2">{owner.name}</Typography>
                  )}
                  <Typography variant="body2">{owner.email || owner.phoneNumber || "—"}</Typography>
                  <Chip
                    label={owner.accountStatus || "ACTIVE"}
                    color={accountStatusColor(owner.accountStatus)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </>
              ) : <Typography variant="body2" color="text.secondary">—</Typography>}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>Actions</Typography>
              <Stack spacing={1} alignItems="flex-start">
                {(product.status === "PUBLISHED" || product.status === "OUT_OF_STOCK") && (
                  <Button variant="outlined" color="error" onClick={() => setStatusDialogOpen(true)}>Delist Product</Button>
                )}
                {product.status === "SUSPENDED" && (
                  <Button variant="outlined" color="success" onClick={() => setStatusDialogOpen(true)}>Restore Product</Button>
                )}
                <Button
                  variant="outlined"
                  disabled={recommendationsMutation.isPending}
                  onClick={() => recommendationsMutation.mutate({ hidden: !product.hiddenFromRecommendations })}
                >
                  {product.hiddenFromRecommendations ? "Show in Feed" : "Hide from Feed"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <ProductStatusDialog
        open={statusDialogOpen}
        currentStatus={product.status}
        onClose={() => setStatusDialogOpen(false)}
        loading={statusMutation.isPending}
        onConfirm={(status) => statusMutation.mutate(status)}
      />

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>{snackbar?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductDetail;
